import { useState, useEffect, useCallback } from 'react';
import { fcmService } from 'utils/fcmService';
import useApi from './useApi';
import useFirebase from './useFirebase';

interface UseFCMReturn {
  hasPermission: boolean;
  isSupported: boolean;
  isLoading: boolean;
  token: string | null;
  error: string | null;
  requestPermission: () => Promise<void>;
  setupMessageListener: () => void;
}

const useFCM = (): UseFCMReturn => {
  const [hasPermission, setHasPermission] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { registerFCMToken, showToast, user } = useApi();
  const { app } = useFirebase();

  // Check if push notifications are supported
  useEffect(() => {
    setIsSupported('Notification' in window && 'serviceWorker' in navigator);
  }, []);
  const getFCMToken = useCallback(async () => {
    if (!app || !user) {
      console.log('FCM: Missing app or user', { app: !!app, user: !!user });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      console.log('FCM: Starting token retrieval...');
      const tokenData = await fcmService.getTokenWithDeviceInfo();
      
      if (tokenData) {
        setToken(tokenData.token);
        console.log('FCM: Token obtained, registering with backend...', tokenData.token.substring(0, 20) + '...');
        
        // Register token with backend
        const payload = {
          fcmToken: tokenData.token,
          ...tokenData.deviceInfo
        };
        
        console.log('FCM: Calling registerFCMToken with payload:', payload);
        await registerFCMToken(payload);
        console.log('FCM token registered successfully');
      } else {
        console.log('FCM: No token data received');
      }    } catch (err: any) {
      console.error('Failed to get FCM token:', err);
      let errorMessage = err.message || 'Failed to register device';
      
      if (err.message?.includes('VAPID key')) {
        errorMessage = 'Push notifications are not configured properly. Please contact support.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [app, user, registerFCMToken]);

  const checkNotificationPermission = useCallback(() => {
    if (!isSupported) return;
    
    const permission = Notification.permission;
    setHasPermission(permission === 'granted');
    
    if (permission === 'granted') {
      getFCMToken();
    }
  }, [isSupported, getFCMToken]);

  // Initialize FCM when Firebase app is ready
  useEffect(() => {
    if (app && isSupported) {
      try {
        fcmService.initialize(app);
        checkNotificationPermission();
      } catch (err: any) {
        console.error('FCM initialization failed:', err);
        setError(err.message);
      }
    }
  }, [app, isSupported, checkNotificationPermission]);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Push notifications are not supported by this browser');
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const permission = await fcmService.requestPermission();
      
      if (permission === 'granted') {
        setHasPermission(true);
        await getFCMToken();
        showToast('Push notifications enabled successfully!', 'success');
      } else if (permission === 'denied') {
        setHasPermission(false);
        throw new Error('Notification permission denied. Please enable notifications in your browser settings.');
      } else {
        setHasPermission(false);
        throw new Error('Notification permission was dismissed.');
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, getFCMToken, showToast]);
  const setupMessageListener = useCallback(() => {
    if (!app || !hasPermission) return;

    return fcmService.setupMessageListener((payload) => {
      console.log('Foreground message received:', payload);
      
      // Show toast notification for foreground messages
      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || 'You have a new message';
      
      showToast(`${title}: ${body}`, 'info', {
        autoHideDuration: 6000
      });
    });
  }, [app, hasPermission, showToast]);
  return {
    hasPermission,
    isSupported,
    isLoading,
    token,
    error,
    requestPermission,
    setupMessageListener
  };
};

export default useFCM;
